import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { ClipLoader } from 'react-spinners';

import { PasswordStrength } from 'components/PasswordStrength';
import { InlineError } from 'ui/InlineError';
import { TotemButton } from 'ui/Button';

import { handleTextInput } from 'utils';

import { TotemLabel, TotemInput } from 'styles';
import { SettingsCredentialsSubtitle, CredentialsSubtitleContainer } from 'styles/pages/settings.styles';

import { analyticsEvents, track } from 'tracking/segment';
import { useChangePasswordMutation, useCheckPasswordMutation } from 'data/__generated__';

export const ChangePassword = () => {
    const [passwordValues, setPasswordValues] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
    const [checkPassword, { loading: checkLoading }] = useCheckPasswordMutation();
    const [changePassword, { loading: passwordLoading }] = useChangePasswordMutation();
    const {
        register,
        handleSubmit,
        formState: { errors },
        setError,
        clearErrors,
        setValue,
    } = useForm({ reValidateMode: 'onSubmit' });

    const isLoading = checkLoading || passwordLoading;

    const handleResetPassword = async () => {
        const { currentPassword, newPassword, confirmPassword } = passwordValues;
        const mediumRegex = new RegExp(
            '^(((?=.*[a-z])(?=.*[A-Z]))|((?=.*[a-z])(?=.*[0-9]))|((?=.*[A-Z])(?=.*[0-9])))(?=.{6,})',
        );

        if (!newPassword.match(mediumRegex)) {
            const message = 'Essayez un mot de passe avec une majuscule ou chiffre et au moins 6 caractères';
            setError('newPassword', { type: 'weakPassword', message });
            return;
        }

        if (newPassword !== confirmPassword) {
            const message = 'Les mots de passe sont différents';
            setError('confirmPassword', { type: 'notMatch', message });
            return;
        }

        const data = await checkPassword({ variables: { password: currentPassword } });
        const passwordIsCorrect = data?.data?.checkPassword;
        if (!passwordIsCorrect) {
            const message = 'Mot de passe incorrect';
            setError('currentPassword', { type: 'invalid', message });
            return;
        }
        const datapassword = await changePassword({
            variables: { password: newPassword },
        });
        const response = datapassword?.data?.changePassword;
        if (response?.error) {
            setError('currentPassword', { type: 'invalid', message: response.error });
        } else if (response?.success) {
            track(analyticsEvents.UPDATE_PASSWORD);
            setPasswordValues({ currentPassword: '', newPassword: '', confirmPassword: '' });
            setValue('currentPassword', '');
            setValue('newPassword', '');
            setValue('confirmPassword', '');
        }
    };

    const handlePasswordInputs = (e: React.ChangeEvent<HTMLInputElement>) => {
        clearErrors(e.target.name);
        handleTextInput(e, setPasswordValues, passwordValues);
    };

    const passwordFieldsFilled =
        passwordValues.currentPassword && passwordValues.newPassword && passwordValues.confirmPassword;

    return (
        <form onSubmit={handleSubmit(handleResetPassword)} autoComplete="off">
            <CredentialsSubtitleContainer $topMargin>
                <SettingsCredentialsSubtitle>Changer le mot de passe</SettingsCredentialsSubtitle>
                <TotemButton $fontSize="1em" disabled={!passwordFieldsFilled} type="submit" $isLoading={isLoading}>
                    {isLoading ? <ClipLoader color="white" size="16px" /> : 'Mettre à jour'}
                </TotemButton>
            </CredentialsSubtitleContainer>
            <TotemLabel>
                Mot de passe actuel <InlineError display={errors.currentPassword} />
                <TotemInput
                    placeholder="••••••••"
                    type="password"
                    autoComplete="current-password"
                    {...register('currentPassword', { required: true })}
                    onChange={handlePasswordInputs}
                />
            </TotemLabel>
            <TotemLabel>
                Nouveau mot de passe <InlineError display={errors.newPassword} />
                <TotemInput
                    placeholder="••••••••"
                    type="password"
                    autoComplete="new-password"
                    {...register('newPassword', { required: true })}
                    onChange={handlePasswordInputs}
                />
                <PasswordStrength input={passwordValues.newPassword} />
            </TotemLabel>
            <TotemLabel>
                Confirmez le nouveau mot de passe <InlineError display={errors.confirmPassword} />
                <TotemInput
                    placeholder="••••••••"
                    type="password"
                    autoComplete="new-password"
                    {...register('confirmPassword', { required: true })}
                    onChange={handlePasswordInputs}
                />
            </TotemLabel>
        </form>
    );
};
